import {
	APP_BASE_DOMAIN,
	getSharedConfig,
	isTenantSlug,
	TenantSlug,
} from "@repo/config/sharedConfig";
import {
	TENANT_DISPLAY_NAMES,
	TENANT_SPRUCE_ORG_IDS,
} from "@repo/config/tenants";

const sharedConfig = getSharedConfig(import.meta.env.MODE);
const IS_PROD = sharedConfig.environment === "production";

export { TENANT_DISPLAY_NAMES, TENANT_SPRUCE_ORG_IDS };

function extractTenantSlugFromPath(pathname: string): TenantSlug | undefined {
	const firstSegment = pathname.split("/").filter(Boolean)[0];
	return isTenantSlug(firstSegment) ? firstSegment : undefined;
}

function extractTenantSlugFromHostname(
	hostname: string,
): TenantSlug | undefined {
	const suffix = `.${APP_BASE_DOMAIN}`;
	if (!hostname.endsWith(suffix)) return undefined;
	const slug = hostname.slice(0, -suffix.length);
	// A leftover dot means a deeper host than <tenant>.app.skylight.health
	if (slug.includes(".")) return undefined;
	return isTenantSlug(slug) ? slug : undefined;
}

/**
 * Get current tenant slug.
 * - Production: from subdomain (hhb.app.skylight.health -> hhb)
 * - Development: from first path segment (/hhb/task -> hhb)
 */
export function getTenantSlug(): TenantSlug | undefined {
	if (IS_PROD) {
		return extractTenantSlugFromHostname(window.location.hostname);
	}
	return extractTenantSlugFromPath(window.location.pathname);
}

/**
 * Get the base path for routing.
 * - Production: "" (tenant is in subdomain)
 * - Development: "/hhb" (tenant is in path)
 */
export function getBasePath(): string {
	if (IS_PROD) return "";
	const tenant = getTenantSlug();
	return tenant ? `/${tenant}` : "";
}

/**
 * Build a full URL path including tenant prefix (for dev).
 * Use this when you need a full path with the tenant prefix for redirects.
 */
export function buildTenantPath(path: string): string {
	const basePath = getBasePath();
	if (!basePath) return path;
	return `${basePath}${path.startsWith("/") ? path : `/${path}`}`;
}

/**
 * URL for the same page on another tenant: production swaps the subdomain,
 * localhost swaps the first path segment.
 */
export function urlForTenantSlug(slug: string): string {
	return buildUrlForTenantSlug(slug, window.location, IS_PROD);
}

export function buildUrlForTenantSlug(
	slug: string,
	location: {
		pathname: string;
		search: string;
		hash: string;
		protocol: string;
		host: string;
	},
	isProd: boolean,
): string {
	const { pathname, search, hash, protocol, host } = location;
	if (isProd) {
		return `${protocol}//${slug}.${APP_BASE_DOMAIN}${pathname}${search}${hash}`;
	}
	const segments = pathname.split("/");
	const first = segments[1];
	if (first && isTenantSlug(first)) {
		segments[1] = slug;
	} else {
		segments.splice(1, 0, slug);
	}
	return `${protocol}//${host}${segments.join("/")}${search}${hash}`;
}

/**
 * Org picker order: Demo and Skylight first, then every other live slug.
 * New TenantSlug values show up without editing the picker.
 */
export function tenantPickerOrder(): TenantSlug[] {
	const pinned = [TenantSlug.Demo, TenantSlug.Skylight];
	const remaining = Object.values(TenantSlug).filter(
		(slug) => !pinned.includes(slug),
	);
	return [...pinned, ...remaining];
}

/**
 * A parsed WRONG_TENANT ORPC body, or undefined when the response is some other
 * error. Handles the unwrapped error object and the `{ json: ... }` Standard RPC
 * envelope.
 *
 * Distinguishing "not a WRONG_TENANT" from "WRONG_TENANT with no slug we can
 * route to" is still useful for tests and logs. The client signs out either
 * way and shows this tenant's login.
 */
export function parseWrongTenantError(
	body: unknown,
): { tenantSlug: TenantSlug | undefined } | undefined {
	const payload = wrongTenantPayload(body);
	if (!payload) return undefined;
	return { tenantSlug: wrongTenantSlug(payload) };
}

function wrongTenantSlug(
	payload: Record<string, unknown>,
): TenantSlug | undefined {
	const data = asRecord(payload.data);
	if (!data) return undefined;
	const nested = asRecord(data.json) ?? data;
	const slug = nested.tenantSlug;
	if (typeof slug !== "string") return undefined;
	return isTenantSlug(slug) ? slug : undefined;
}

function wrongTenantPayload(
	body: unknown,
): Record<string, unknown> | undefined {
	const root = asRecord(body);
	if (!root) return undefined;
	const payload = root.code === "WRONG_TENANT" ? root : asRecord(root.json);
	if (!payload || payload.code !== "WRONG_TENANT") return undefined;
	return payload;
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
	if (!value || typeof value !== "object") return undefined;
	return value as Record<string, unknown>;
}
